/**
 * Feature Flags Configuration
 *
 * Static defaults loaded from env vars.
 * Use FeatureFlagsContext for runtime toggles (dev tools only).
 */

export interface FeatureFlags {
  /** Azure AD SSO — disabled in dev by default */
  ssoEnabled: boolean;
  /**
   * Global revamp toggle — switches every V2 page/shell on or off together.
   * Off in production. Defaults to on when dev tools are enabled; can be
   * overridden at runtime via DevFlagToggle (persisted in localStorage).
   */
  useRevamp: boolean;
}

const parseBoolean = (value: string | undefined, defaultValue: boolean = false): boolean => {
  if (!value) return defaultValue;
  return ['true', '1', 'yes'].includes(value.toLowerCase());
};

const DEV_HOSTNAMES = ['localhost', '127.0.0.1', 'alloc-dev.ab-inbev.com'];

const isDevHost =
  typeof window !== 'undefined' &&
  DEV_HOSTNAMES.some((h) => window.location.hostname === h);

/**
 * True when developer tooling should be available:
 *   - local dev server (`npm run dev`)
 *   - deployed DEV environment (hostname in DEV_HOSTNAMES)
 *   - any build with VITE_SHOW_DEV_TOOLS=true (pipeline opt-in)
 * Always false in production.
 */
export const isDevToolsEnabled: boolean =
  import.meta.env.DEV || isDevHost || import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';

export const defaultFeatureFlags: FeatureFlags = {
  ssoEnabled: parseBoolean(import.meta.env.VITE_SSO_ENABLED, true),
  // Dev tools on → revamp on by default (user can toggle via DevFlagToggle).
  // Dev tools off (production) → old alloc always.
  useRevamp: isDevToolsEnabled,
};

/** Backward-compatible alias — use defaultFeatureFlags for new code */
export const featureFlags = defaultFeatureFlags;

export const isFeatureEnabled = (flag: keyof FeatureFlags): boolean => featureFlags[flag];

if (isDevToolsEnabled) {
  console.log('🚩 Feature Flags (defaults):', defaultFeatureFlags);
}
